import { useEffect, useState } from "react";
import { Upload, FileText, Trash2, Download, Loader2, Lock, Brain, BrainCircuit } from "lucide-react";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const Mono = ({ children, className = "" }) => (
  <span className={`font-mono uppercase tracking-[0.2em] ${className}`}>{children}</span>
);

const fileUrl = (id) => `${API}/library/documents/${id}/file?token=${localStorage.getItem("mdau_token")}`;

export default function DocumentLibrary() {
  const { user } = useAuth();
  const isStaff = ["admin", "staff"].includes(user?.role);
  const [meta, setMeta] = useState(null);
  const [state, setState] = useState(null);
  const [docs, setDocs] = useState([]);
  const [error, setError] = useState("");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [form, setForm] = useState({ title: "", category: "Other", state: "NSW", file: null });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/library/meta").then((r) => {
      setMeta(r.data);
      setState(r.data.default_state);
      setForm((f) => ({ ...f, state: r.data.default_state }));
    }).catch(() => {});
  }, []);

  const load = (st) => api.get(`/library/documents${st ? `?state=${st}` : ""}`).then((r) => setDocs(r.data)).catch(() => {});
  useEffect(() => { if (state) load(state); }, [state]);

  const upload = async (e) => {
    e.preventDefault();
    if (!form.file) return;
    setBusy(true); setError("");
    try {
      const fd = new FormData();
      fd.append("file", form.file);
      fd.append("title", form.title || form.file.name);
      fd.append("category", form.category);
      fd.append("state", form.state);
      await api.post("/library/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUploadOpen(false);
      setForm((f) => ({ ...f, title: "", file: null }));
      load(state);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/library/documents/${id}`); load(state); }
    catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const toggleLearning = async (d) => {
    try {
      await api.patch(`/library/documents/${d.id}/learning`, { enabled: d.learn_enabled === false });
      load(state);
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  return (
    <div className="space-y-8 text-white" data-testid="library-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <Mono className="text-gold-400 text-[0.65rem]">Sheet 10 — Document Library</Mono>
          <h1 className="font-heading font-light text-4xl tracking-tight mt-3">Reference & council documents</h1>
          <p className="text-white/55 mt-3 max-w-2xl font-light">
            NSWRA / NDP guidelines, fact sheets, templates and benchmark rates — plus your own council documents. The AI Expert answers grounded in this library.
          </p>
        </div>
        <button onClick={() => setUploadOpen(true)} data-testid="library-upload-btn"
          className="btn-orb-plate px-5 py-2.5 rounded-md text-sm">
          <Upload className="h-4 w-4" /> Upload document
        </button>
      </div>

      {/* state selector */}
      <div className="flex flex-wrap items-center gap-2" data-testid="library-state-selector">
        {meta?.states?.map((s) => (
          <button key={s} onClick={() => setState(s)} data-testid={`state-${s}`}
            className={`font-mono text-[0.65rem] tracking-widest px-3 py-1.5 rounded-sm border transition-colors ${state === s ? "bg-gold-500 text-navy-950 border-gold-500 font-bold" : "border-gold-500/25 text-white/60 hover:text-white hover:border-gold-500/50"}`}>
            {s}
          </button>
        ))}
      </div>

      {error && <div className="bg-red-500/10 border border-red-500/30 text-red-300 text-sm px-4 py-3 rounded-sm">{error}</div>}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="library-docs">
        {docs.length === 0 && <div className="text-white/40 text-sm col-span-full py-10 text-center">No documents for {state}.</div>}
        {docs.map((d) => (
          <div key={d.id} data-testid={`doc-${d.id}`} className="border border-gold-500/20 bg-navy-900/40 p-5 rounded-sm flex flex-col">
            <div className="flex items-start justify-between gap-3">
              <FileText className="h-5 w-5 text-gold-500/80 shrink-0" />
              <div className="flex items-center gap-2">
                <Mono className="text-[0.5rem] text-white/40">{d.state}</Mono>
                {d.source === "reference"
                  ? <span className="font-mono text-[0.45rem] uppercase tracking-widest text-gold-400/80 border border-gold-500/30 px-1.5 py-0.5 rounded-sm flex items-center gap-1"><Lock className="h-2.5 w-2.5" />Reference</span>
                  : <span className="font-mono text-[0.45rem] uppercase tracking-widest text-emerald-400/80 border border-emerald-500/30 px-1.5 py-0.5 rounded-sm">Council</span>}
              </div>
            </div>
            <div className="font-heading text-base mt-3 leading-snug">{d.title}</div>
            <Mono className="text-[0.5rem] text-white/35 mt-1">{d.category}</Mono>
            <div className="mt-4 pt-3 border-t border-white/10 flex items-center gap-4">
              <a href={fileUrl(d.id)} target="_blank" rel="noreferrer" data-testid={`doc-download-${d.id}`}
                className="inline-flex items-center gap-1.5 text-xs text-gold-400 hover:text-gold-300">
                <Download className="h-3.5 w-3.5" /> Open
              </a>
              {isStaff ? (
                <button onClick={() => toggleLearning(d)} data-testid={`doc-learning-${d.id}`}
                  title={d.learn_enabled === false ? "Excluded from AI Expert — click to include" : "AI Expert learns from this — click to exclude"}
                  className={`inline-flex items-center gap-1.5 text-xs transition-colors ${d.learn_enabled === false ? "text-white/35 hover:text-white/60" : "text-emerald-400 hover:text-emerald-300"}`}>
                  <BrainCircuit className="h-3.5 w-3.5" /> {d.learn_enabled === false ? "AI off" : "AI learning"}
                </button>
              ) : (
                d.learn_enabled !== false && (
                  <span className="inline-flex items-center gap-1.5 text-xs text-emerald-400/70" data-testid={`doc-learning-badge-${d.id}`}>
                    <Brain className="h-3.5 w-3.5" /> AI learning
                  </span>
                )
              )}
              {d.source === "upload" && (
                <button onClick={() => remove(d.id)} data-testid={`doc-delete-${d.id}`}
                  className="inline-flex items-center gap-1.5 text-xs text-white/40 hover:text-red-400">
                  <Trash2 className="h-3.5 w-3.5" /> Delete
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {uploadOpen && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setUploadOpen(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={upload} data-testid="library-upload-form"
            className="bg-navy-900 border border-gold-500/25 rounded-md p-6 w-full max-w-md space-y-4">
            <div className="font-heading text-xl">Upload document</div>
            <div>
              <label className="font-mono uppercase tracking-[0.18em] text-white/50 text-[0.6rem] block mb-1">Title</label>
              <input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} data-testid="upload-title"
                className="w-full bg-navy-950 border border-gold-500/25 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-gold-500/60" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="font-mono uppercase tracking-[0.18em] text-white/50 text-[0.6rem] block mb-1">Category</label>
                <select value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} data-testid="upload-category"
                  className="w-full bg-navy-950 border border-gold-500/25 rounded-sm px-3 py-2 text-sm focus:outline-none">
                  {meta?.categories?.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="font-mono uppercase tracking-[0.18em] text-white/50 text-[0.6rem] block mb-1">State</label>
                <select value={form.state} onChange={(e) => setForm((f) => ({ ...f, state: e.target.value }))} data-testid="upload-state"
                  className="w-full bg-navy-950 border border-gold-500/25 rounded-sm px-3 py-2 text-sm focus:outline-none">
                  {meta?.states?.map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="font-mono uppercase tracking-[0.18em] text-white/50 text-[0.6rem] block mb-1">File (PDF, XLSX, CSV, TXT, MD)</label>
              <input type="file" accept=".pdf,.xlsx,.xlsm,.csv,.txt,.md" onChange={(e) => setForm((f) => ({ ...f, file: e.target.files[0] }))} data-testid="upload-file"
                className="w-full text-sm text-white/70 file:mr-3 file:py-2 file:px-3 file:rounded-sm file:border-0 file:bg-gold-500 file:text-navy-950 file:font-semibold" required />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={() => setUploadOpen(false)} className="text-sm text-white/60 px-4 py-2">Cancel</button>
              <button type="submit" disabled={busy} data-testid="upload-submit" className="btn-orb-plate px-5 py-2.5 rounded-md text-sm disabled:opacity-60">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} Upload
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
